<template>
  <div
    class="fixed bottom-0 inset-x-0 px-4 pb-4 sm:inset-0 sm:flex sm:items-center sm:justify-center"
  >
    <!--
    Background overlay, show/hide based on modal state.

    Entering: "ease-out duration-300"
      From: "opacity-0"
      To: "opacity-100"
    Leaving: "ease-in duration-200"
      From: "opacity-100"
      To: "opacity-0"
  -->
    <div class="fixed inset-0 transition-opacity">
      <div class="absolute inset-0 bg-gray-500 opacity-75" @click="close"></div>
    </div>

    <!--
    Modal panel, show/hide based on modal state.

    Entering: "ease-out duration-300"
      From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      To: "opacity-100 translate-y-0 sm:scale-100"
    Leaving: "ease-in duration-200"
      From: "opacity-100 translate-y-0 sm:scale-100"
      To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
  -->
    <div
      class="relative bg-white rounded-lg px-4 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-headline"
    >
      <div class="sm:flex sm:items-start">
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3
            class="text-lg leading-6 font-medium text-gray-900"
            id="modal-headline"
          >
            <span v-if="!agent"> Add a new number </span>
            <span v-if="agent"> Edit a number </span>
          </h3>
          <div class="mt-2"></div>
        </div>
      </div>
      <div class="sm:ml-4 mt-4">
        <div>
          <label
            for="email"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Name
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              class="bg-white focus:outline-none focus:shadow-outline border border-gray-300 rounded-md py-2 px-4 block w-full appearance-none leading-normal"
              type="text"
              v-model="$v.form.name.$model"
              ref="name"
              placeholder="Your agent name"
            />
            <p v-if="!$v.form.name.maxLength" class="mt-2 text-sm text-red-600">
              Name should be less than
              {{ $v.form.name.$params.maxLength.max }} characters
            </p>
          </div>
        </div>
        <div class="mt-4">
          <label
            for="email"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Role
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              class="bg-white focus:outline-none focus:shadow-outline border border-gray-300 rounded-md py-2 px-4 block w-full appearance-none leading-normal"
              type="text"
              v-model="$v.form.description.$model"
              ref="role"
              placeholder="Customer support"
            />
            <p
              v-if="!$v.form.description.maxLength"
              class="mt-2 text-sm text-red-600"
            >
              Role should be less than
              {{ $v.form.description.$params.maxLength.max }} characters
            </p>
          </div>
        </div>
        <div class="mt-4">
          <label
            for="email"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Phone Number
          </label>
          <div class="mt-1  rounded-md shadow-sm">
            <vue-tel-input
              :value="form.phoneNumber"
              mode="international"
              @input="handlePhoneNumberUpdate"
            />
          </div>
        </div>
        <div class="my-4">
          <label
            for="email"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Avatar
          </label>
          <div class="my-4 relative rounded-md flex">
            <input
              v-model="form.avatarUrl"
              type="radio"
              id="avatar-male"
              value="https://cdn.shopify.com/s/files/1/0539/8446/0950/t/1/assets/avatar-male.svg"
            />
            <label for="avatar-male">
              <img
                class="w-10 rounded-full bg-green-700"
                src="https://cdn.shopify.com/s/files/1/0539/8446/0950/t/1/assets/avatar-male.svg"
              />
            </label>
            <input
              v-model="form.avatarUrl"
              type="radio"
              id="avatar-female"
              value="https://cdn.shopify.com/s/files/1/0539/8446/0950/t/1/assets/avatar-female-2.svg"
            />
            <label for="avatar-female">
              <img
                class="w-10 rounded-full bg-green-700"
                src="https://cdn.shopify.com/s/files/1/0539/8446/0950/t/1/assets/avatar-female-2.svg"
              />
            </label>
          </div>
        </div>
      </div>

      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
            v-if="!agent"
            :disabled="$v.form.$invalid"
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-indigo-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50"
            @click="onCreate"
          >
            Add
          </button>
          <button
            v-if="agent"
            :disabled="$v.form.$invalid"
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-indigo-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50"
            @click="onUpdate"
          >
            Update
          </button>
        </span>
        <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
            @click="close"
          >
            Cancel
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { VueTelInput } from "vue-tel-input";
import { required, maxLength } from "vuelidate/lib/validators";

export default {
  components: {
    VueTelInput
  },
  props: {
    agent: {
      type: Object
    }
  },

  data() {
    return {
      form: {
        name: "",
        description: "",
        avatarUrl:
          "https://cdn.shopify.com/s/files/1/0539/8446/0950/t/1/assets/avatar-male.svg",
        phoneNumber: ""
      }
    };
  },

  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(20)
      },
      description: {
        required,
        maxLength: maxLength(20)
      },
      phoneNumber: {
        required
      }
    }
  },
  computed: {
    hasMoreTitleCharacters() {
      const label = this.form.name.widgetTitle;
      return label.length > 20;
    }
  },
  mounted() {
    if (this.agent) {
      this.form = this.agent;
    }

    setTimeout(() => {
      this.$refs.name.focus();
    }, 100);
  },
  methods: {
    close() {
      this.$emit("close");
    },
    onCreate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.$emit("create", { ...this.form });
      this.close();
    },

    onUpdate() {
      this.$emit("edit", { ...this.form });
      this.close();
    },

    handlePhoneNumberUpdate(phoneNumber, { number }) {
      // if (isValid) {
        this.form.phoneNumber = number.e164;
      // }
    }
  }
};
</script>

<style lang="scss">
.vti__dropdown-list {
  max-height: 150px !important;
}
</style>
